import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import httpClient from '../../config/httpClient';
import firebase from '@config/firebase.config';
import styles from './index.module.scss';
import { Modal } from '@/components/Modal';
import { CancelSubscriptionModal } from './CancelSubscriptionModal.component';
import { addNotification, notificationStates } from '@/components/Notification';
import { ChangeSubscriptionModal } from './ChangeSubscriptionModal.component';
import { ContactUsModal } from '@/modals/ContactUs/ContactUsModal.component';
import { CheckoutSeatsPlanModal } from './CheckoutSeatsPlanModal.component';
import { nonActiveSubscriptionStates } from '@/layouts';
import { ExistingSubscriptionReplaceModal } from './ExistingSubscriptionReplaceModal.component';
import { ModifySeatsModal } from './ModifySeatsModal.component';
import { Spinner } from '@/components/Spinner/Spinner.component';
import { DomesticPaymentMethodWarningModal } from './DomesticPaymentMethodWarningModal.component';
import { appRoutes } from '@/routes';
import { PlanCard } from '@/components/PlanCard';
import { FeatureComparisonTable } from '@/components/FeatureComparisonTable';
import { BillingCycleSelector } from '@/components/BillingCycleSelector';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { featureCategories } from '@/constants/pricingPlanFeatures';
import {
  BILLING_CYCLE_CONFIG,
  CURRENCIES,
  PLAN_TIERS,
  TIER_PRICING,
  getPlanId,
  getPlanIdsForCurrency,
} from '@/config/pricing';
const database = firebase.firestore();

const fetchGeoJs = async () => {
  const res = await fetch('https://get.geojs.io/v1/ip/geo.json', {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    throw new Error(`GeoJS request failed (${res.status})`);
  }
  return res.json();
};

const getCurrencyFromCountryCode = (countryCode) => {
  const normalized = String(countryCode || '').toUpperCase();
  return normalized === 'IN' ? CURRENCIES.INR : CURRENCIES.USD;
};

const getPlanIdForCurrency = ({ tierId, billingCycle, currencyCode }) =>
  getPlanId(tierId, billingCycle, currencyCode);

const SubscriptionPage = () => {
  const [isContactUsModalOpen, setIsContactUsModalOpen] = useState(false);
  const [isCancelSubscriptionModalOpen, setIsCancelSubscriptionModalOpen] =
    useState(false);
  const [isChangeSubscriptionModalOpen, setIsChangeSubscriptionModalOpen] =
    useState(false);
  const [isCheckoutSeatsPlanModalOpen, setIsCheckoutSeatsPlanModalOpen] =
    useState(false);
  const [
    isDomesticPaymentMethodWarningModalOpen,
    setIsDomesticPaymentMethodWarningModalOpen,
  ] = useState(false);
  const [
    isExistingSubscriptionReplaceModalOpen,
    setIsExistingSubscriptionReplaceModalOpen,
  ] = useState(false);
  const [isModifySeatsModalOpen, setIsModifySeatsModalOpen] = useState(false);
  const [selectedBillingCycle, setSelectedBillingCycle] = useState('monthly');
  const [selectedPlan, setSelectedPlan] = useState({ id: PLAN_TIERS.GROW });
  const [isLoading, setIsLoading] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [geoCountryCode, setGeoCountryCode] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [pendingCurrency, setPendingCurrency] = useState(null);
  const [isCurrencyModalOpen, setIsCurrencyModalOpen] = useState(false);
  const [geoLookupFailed, setGeoLookupFailed] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const authState = useSelector((state) => ({ ...state.auth }));
  const companyId = authState.profile.activeCompany.companyId;
  const isEnterpriseQueryRef = useRef(false);
  const subscriptionIdToCancelRef = useRef(null);
  const canRenderPlans = Boolean(selectedCurrency) && !isCurrencyModalOpen;
  const {
    currentActivePlanId,
    currentInActivePlanId,
    showCycleEndWarning,
    subscriptionEndDate,
    cancelledAt,
    domesticPaymentMethod,
  } = useMemo(() => {
    let activePlanId = null,
      inactivePlanId = null,
      showCycleEndWarning = false;
    if (subscriptionData?.status === 'active') {
      activePlanId = subscriptionData?.planId;
    } else {
      inactivePlanId = subscriptionData?.planId;
    }
    const subscriptionEndDate = subscriptionData?.subscriptionEndDate
      ?.toDate()
      .toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    const cancelledAt = subscriptionData?.cancelledAt
      ?.toDate()
      .toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    const formattedSubscriptionEndDate = subscriptionEndDate
      ? subscriptionEndDate
      : null;
    const formattedCancelledAt = cancelledAt ? cancelledAt : null;
    if (nonActiveSubscriptionStates.includes(subscriptionData?.status)) {
      showCycleEndWarning = true;
    }
    return {
      currentActivePlanId: activePlanId,
      currentInActivePlanId: inactivePlanId,
      subscriptionEndDate: formattedSubscriptionEndDate,
      showCycleEndWarning,
      cancelledAt: formattedCancelledAt,
      domesticPaymentMethod: subscriptionData?.domesticPaymentMethod,
    };
  }, [subscriptionData]);

  useEffect(() => {
    if (authState?.profile?.subscription) {
      setSubscriptionData(authState?.profile?.subscription);
    }
  }, [authState?.profile?.subscription]);

  useEffect(() => {
    let cancelled = false;
    fetchGeoJs()
      .then((data) => {
        if (cancelled) return;
        const countryCode = data?.country_code || null;
        console.log('GeoJS location info:', data);
        console.log('GeoJS country_code:', countryCode);
        setGeoCountryCode(countryCode);
        const detectedCurrency = getCurrencyFromCountryCode(countryCode);
        setSelectedCurrency(detectedCurrency);
        setPendingCurrency(detectedCurrency);
        setGeoLookupFailed(false);
      })
      .catch((err) => {
        console.warn('GeoJS lookup failed:', err?.message || err);
        if (cancelled) return;
        setSelectedCurrency(null);
        setPendingCurrency(null);
        setGeoLookupFailed(true);
        setIsCurrencyModalOpen(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Handle success/cancel redirects from Stripe Checkout
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const subscriptionStatus = urlParams.get('subscription');
    const canceled = urlParams.get('canceled');

    if (subscriptionStatus === 'success') {
      alert('Subscription activated successfully!');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    if (canceled) {
      alert('Subscription was canceled.');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);
  const planIdsForCurrency = useMemo(
    () => getPlanIdsForCurrency(selectedCurrency || CURRENCIES.USD),
    [selectedCurrency]
  );

  // Subscription plans data
  const subscriptionTiers = [
    {
      id: PLAN_TIERS.STARTER,
      name: 'Starter',
      description: 'Ideal for Growing Buisness',
      previousTierName: null,
      features: [
        'Advanced time tracking',
        'Idle Time tracking',
        'Screenshots',
        'Advance Reporting and notification',
        'Support : Mail & Support Ticket',
      ],
      pricing: TIER_PRICING[PLAN_TIERS.STARTER],
      popular: false,
      razorpayPlanId: {
        monthly: planIdsForCurrency.starter.monthly,
        quarterly: planIdsForCurrency.starter.quarterly,
        yearly: planIdsForCurrency.starter.yearly,
      },
    },
    {
      id: PLAN_TIERS.GROW,
      name: 'Grow',
      description: 'Ideal for growing businesses',
      previousTierName: 'Starter',
      features: [
        'App Usage tracking',
        'Attendance tracker',
        'Custom task control',
        'Leave Management',
        'Support : Mail, Phone & Support Ticket',
      ],
      pricing: TIER_PRICING[PLAN_TIERS.GROW],
      popular: true,
      razorpayPlanId: {
        monthly: planIdsForCurrency.grow.monthly,
        quarterly: planIdsForCurrency.grow.quarterly,
        yearly: planIdsForCurrency.grow.yearly,
      },
    },
    {
      id: PLAN_TIERS.ENTERPRISE,
      name: 'Enterprise',
      description: 'Built for larger teams and organizations',
      previousTierName: 'Grow',
      features: [
        'Custom Integration',
        'Advance company level settings',
        'Advance Security',
        'Support : Dedicated Account Manager',
      ],
      popular: false,
      razorpayPlanId: {
        monthly: null,
        quarterly: null,
        yearly: null,
      },
    },
  ];

  const billingCycleOptions = Object.entries(BILLING_CYCLE_CONFIG).map(
    ([value, { label, sublabel, discountPercent }]) => ({
      value,
      label,
      sublabel,
      discountPercent,
    })
  );

  const comparisonPlans = subscriptionTiers.map((tier) => ({
    key: tier.id,
    label: tier.name,
    popular: tier.popular,
  }));

  // Handle plan selection
  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
  };

  // Handle billing cycle change
  const handleBillingCycleChange = (cycle) => {
    setSelectedBillingCycle(cycle);
  };

  const handleCurrencyChoice = (currencyCode) => {
    setPendingCurrency(currencyCode);
  };

  const handleConfirmCurrencyChoice = () => {
    if (!pendingCurrency) return;
    setSelectedCurrency(pendingCurrency);
    setIsCurrencyModalOpen(false);
  };

  const planToChangeRef = useRef(null);
  const chosenPlanRef = useRef(null);
  const addModifySeatsFlag = async () => {
    try {
      await database.collection('companyDatabase').doc(companyId).update({
        'subscriptions.modifySeats': true,
      });
    } catch (error) {
      console.log(error, '>>> index.jsx <<<', 'line-465');
    }
  };
  const handleChangeSubscriptionPlanWithDomesticPaymentMethod = async () => {
    subscriptionIdToCancelRef.current = subscriptionData?.subscriptionId;
    handleRazorpaySubscription({
      plan: planToChangeRef.current,
      totalSeats: subscriptionData?.seats,
      modifySeats: true,
      callback: async () => {
        await handleCancelPlan({
          reload: false,
          subscriptionId: subscriptionIdToCancelRef.current,
          message: 'Previous subscription cancelled successfully.',
        });
        await handleRefundForDomesticPayment();
      },
    });
  };
  const handleChangeSubscriptionPlan = async () => {
    try {
      const plan = planToChangeRef.current;
      if (!plan) {
        addNotification({
          message: 'No plan selected to change.',
          level: notificationStates.warning,
        });
        return;
      }
      if (!selectedCurrency) {
        setIsCurrencyModalOpen(true);
        return;
      }
      setIsLoading(true);
      if (domesticPaymentMethod) {
        handleChangeSubscriptionPlanWithDomesticPaymentMethod();
        return;
      }
      const selectedPlanId = getPlanIdForCurrency({
        tierId: plan.id,
        billingCycle: selectedBillingCycle,
        currencyCode: selectedCurrency,
      });
      if (!selectedPlanId) {
        throw new Error(
          `Razorpay planId missing for tier=${plan.id}, billingCycle=${selectedBillingCycle}`
        );
      }
      const response = await httpClient.post('/changeSubscriptionPlan', {
        subscriptionId: subscriptionData?.subscriptionId,
        planId: selectedPlanId,
        companyId,
      });
      const result = response.data;
      if (result?.success) {
        addNotification({
          message: 'Subscription changed successfully.',
          level: notificationStates.success,
          autoDismiss: 4,
        });
        history.push(`${appRoutes().teamDashboard}?subscription=success`);
        window.location.reload();
      } else {
        addNotification({
          message:
            result?.error ||
            result?.message ||
            'Could not change subscription plan. Please try again.',
          level: notificationStates.error,
        });
      }
    } catch (error) {
      const apiMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Could not change subscription plan. Please try again.';
      addNotification({
        message: apiMessage,
        level: notificationStates.error,
      });
      if (error?.response?.data?.domesticPaymentMethod) {
        setIsDomesticPaymentMethodWarningModalOpen(true);
      }
    } finally {
      setIsLoading(false);
    }
  };
  const handleRazorpaySubscription = async ({
    plan,
    totalSeats = 2,
    callback,
    modifySeats = false,
  }) => {
    try {
      if (!selectedCurrency) {
        setIsCurrencyModalOpen(true);
        return;
      }
      setIsLoading(true);
      const functions = firebase.functions();
      const createRazorpaySubscription = functions.httpsCallable(
        'createRazorpaySubscription'
      );

      const selectedPlanId = getPlanIdForCurrency({
        tierId: plan.id,
        billingCycle: selectedBillingCycle,
        currencyCode: selectedCurrency,
      });
      if (!selectedPlanId) {
        throw new Error(
          `Razorpay planId missing for tier=${plan?.id}, billingCycle=${selectedBillingCycle}`
        );
      }

      const result = await createRazorpaySubscription({
        planId: selectedPlanId,
        companyId: companyId,
        seats: totalSeats,
      });

      if (result?.data?.success) {
        const options = {
          key: process.env.REACT_APP_RAZORPAY_KEY_ID,
          subscription_id: result.data.subscriptionId,
          handler: async (response) => {
            if (response.razorpay_payment_id) {
              if (modifySeats) {
                await addModifySeatsFlag();
              }
              if (callback) {
                await callback();
              }
              window.location.href = `${appRoutes().teamDashboard}?subscription=success&paymentId=${response.razorpay_payment_id}`;
            }
          },
          name: plan.name,
        };
        const rzp = new window.Razorpay(options);
        rzp.open();
      }
      return;
    } catch (error) {
      console.log(error, '>>> index.jsx <<<', 'line-279');
    } finally {
      chosenPlanRef.current = null;
      setIsLoading(false);
    }
  };

  const handleCancelPlan = async ({
    reload = true,
    subscriptionId,
    message,
  }) => {
    try {
      const subscriptionIdToCancel =
        subscriptionId || subscriptionData?.subscriptionId;
      setIsLoading(true);
      const response = await httpClient.post('/cancelSubscriptionPlan', {
        subscriptionId: subscriptionIdToCancel,
      });
      const result = response.data;
      if (result.success) {
        const messageToDisplay =
          message || 'Subscription cancelled successfully.';
        addNotification({
          message: messageToDisplay,
          level: notificationStates.success,
          autoDismiss: 4,
        });
        if (reload) {
          history.push(appRoutes().teamDashboard);
          window.location.reload();
        }
      }
    } catch (error) {
      console.log(error, '>>> index.jsx <<<', 'line-415');
    } finally {
      setIsLoading(false);
    }
  };

  const handleContactUs = () => {
    setIsContactUsModalOpen(true);
  };
  const isSubscriptionCancelled = () => {
    const isInactiveSubscription = nonActiveSubscriptionStates.includes(
      subscriptionData?.status
    );
    const isSubscriptionEndDateInFuture =
      subscriptionData?.subscriptionEndDate?.toDate() > new Date();
    return isInactiveSubscription && isSubscriptionEndDateInFuture;
  };
  const handleRefundForDomesticPayment = async () => {
    try {
      setIsLoading(true);
      const response = await httpClient.post(
        '/initiateRefundForDomesticPayments',
        {
          subscriptionId: subscriptionIdToCancelRef.current,
        }
      );
      const result = response.data;
      if (result.success) {
        addNotification({
          message: 'Refund for domestic payment initiated successfully.',
          level: notificationStates.success,
          autoDismiss: 4,
        });
      }
    } catch (error) {
      addNotification({
        message: 'Failed to initiate refund for domestic payment.',
        level: notificationStates.error,
        autoDismiss: 4,
      });
      console.log(error, '>>> index.jsx <<<', 'line-420');
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {isLoading && <Spinner overlay={true} />}
        {/* Header */}
        <div className={styles.header}>
          {/* <div className={styles.headerTitles}>
            <Text
              variant={textVariantsEnum.Display_xs}
              fontWeight={fontWeightEnum.Bold}
              color="gray-900"
            >
              Subscription
            </Text>
            <Text
              variant={textVariantsEnum.Text_md}
              fontWeight={fontWeightEnum.Normal}
              color="gray-500"
            >
              Choose the perfect plan for your team&apos;s productivity.
            </Text>
          </div> */}

          {/* Billing Cycle Selector */}
          <BillingCycleSelector
            options={billingCycleOptions}
            value={selectedBillingCycle}
            onChange={handleBillingCycleChange}
          />
        </div>

        {/* Plans Grid */}
        {canRenderPlans ? (
          <div className={styles.plansGrid}>
            {subscriptionTiers.map((plan) => (
              <PlanCard
                currentActivePlanId={currentActivePlanId}
                currentInActivePlanId={currentInActivePlanId}
                key={plan.id}
                plan={plan}
                billingCycle={selectedBillingCycle}
                currencyCode={selectedCurrency}
                isSelected={selectedPlan?.id === plan.id}
                onSelect={() => handlePlanSelect(plan)}
                showCycleEndWarning={showCycleEndWarning}
                subscriptionEndDate={subscriptionEndDate}
                cancelledAt={cancelledAt}
                onModifySeats={() => {
                  chosenPlanRef.current = plan;
                  setIsModifySeatsModalOpen(true);
                }}
                onSubscribe={() => {
                  if (typeof currentActivePlanId === 'string') {
                    planToChangeRef.current = plan;
                    if (domesticPaymentMethod) {
                      setIsDomesticPaymentMethodWarningModalOpen(true);
                      return;
                    }
                    setIsChangeSubscriptionModalOpen(true);
                    return;
                  }
                  chosenPlanRef.current = plan;
                  if (isSubscriptionCancelled()) {
                    setIsExistingSubscriptionReplaceModalOpen(true);
                    return;
                  }
                  setIsCheckoutSeatsPlanModalOpen(true);
                  return;
                }}
                isLoading={isLoading}
                onContactUs={() => {
                  isEnterpriseQueryRef.current = true;
                  handleContactUs();
                }}
                onCancelPlan={() => setIsCancelSubscriptionModalOpen(true)}
              />
            ))}
          </div>
        ) : (
          <div className={styles.currencySelectionPlaceholder}>
            Select a currency to view available plans.
          </div>
        )}

        {/* Feature Comparison */}
        <FeatureComparisonTable
          categories={featureCategories}
          plans={comparisonPlans}
        />

        {/* Footer */}
        <div className={styles.footer}>
          <div className={styles.footerText}>
            <p>
              Need help choosing?{' '}
              <span
                onClick={() => {
                  isEnterpriseQueryRef.current = false;
                  handleContactUs();
                }}
              >
                Contact our sales team
              </span>
            </p>
            <p>
              All plans include a 14-day free trial. No credit card required.
            </p>
          </div>
        </div>

        <Modal
          isVisible={isDomesticPaymentMethodWarningModalOpen}
          onClose={() => setIsDomesticPaymentMethodWarningModalOpen(false)}
        >
          <DomesticPaymentMethodWarningModal
            onClose={() => setIsDomesticPaymentMethodWarningModalOpen(false)}
            onConfirm={() => {
              setIsDomesticPaymentMethodWarningModalOpen(false);
              setIsChangeSubscriptionModalOpen(true);
            }}
          />
        </Modal>
        <Modal
          isVisible={isCancelSubscriptionModalOpen}
          onClose={() => setIsCancelSubscriptionModalOpen(false)}
        >
          <CancelSubscriptionModal
            onClose={() => setIsCancelSubscriptionModalOpen(false)}
            onConfirm={() => {
              handleCancelPlan({
                reload: true,
              });
              setIsCancelSubscriptionModalOpen(false);
            }}
          />
        </Modal>
        <Modal
          isVisible={isChangeSubscriptionModalOpen}
          onClose={() => setIsChangeSubscriptionModalOpen(false)}
        >
          <ChangeSubscriptionModal
            newPlan={planToChangeRef.current}
            subscriptionData={subscriptionData}
            billingCycle={selectedBillingCycle}
            currencyCode={selectedCurrency}
            onClose={() => {
              setIsChangeSubscriptionModalOpen(false);
              planToChangeRef.current = null;
            }}
            onConfirm={() => {
              setIsChangeSubscriptionModalOpen(false);
              handleChangeSubscriptionPlan();
            }}
          />
        </Modal>
        <Modal
          isVisible={isContactUsModalOpen}
          onClose={() => setIsContactUsModalOpen(false)}
        >
          <ContactUsModal
            onClose={() => setIsContactUsModalOpen(false)}
            isEnterpriseQuery={isEnterpriseQueryRef.current}
          />
        </Modal>
        <Modal
          isVisible={isExistingSubscriptionReplaceModalOpen}
          onClose={() => setIsExistingSubscriptionReplaceModalOpen(false)}
        >
          <ExistingSubscriptionReplaceModal
            onConfirm={() => {
              setIsExistingSubscriptionReplaceModalOpen(false);
              setIsCheckoutSeatsPlanModalOpen(true);
            }}
            onClose={() => setIsExistingSubscriptionReplaceModalOpen(false)}
          />
        </Modal>
        <Modal
          isVisible={isCheckoutSeatsPlanModalOpen}
          onClose={() => setIsCheckoutSeatsPlanModalOpen(false)}
        >
          <CheckoutSeatsPlanModal
            subscriptionData={subscriptionData}
            plan={chosenPlanRef.current}
            billingCycle={selectedBillingCycle}
            currency={selectedCurrency}
            onClose={() => setIsCheckoutSeatsPlanModalOpen(false)}
            onConfirm={(totalSeats) => {
              setIsCheckoutSeatsPlanModalOpen(false);
              handleRazorpaySubscription({
                plan: chosenPlanRef.current,
                totalSeats,
              });
            }}
          />
        </Modal>
        <Modal
          isVisible={isModifySeatsModalOpen}
          onClose={() => setIsModifySeatsModalOpen(false)}
        >
          <ModifySeatsModal
            onClose={() => setIsModifySeatsModalOpen(false)}
            subscriptionData={subscriptionData}
            plan={chosenPlanRef.current}
            billingCycle={selectedBillingCycle}
            currency={selectedCurrency}
            onConfirm={(totalSeats) => {
              setIsModifySeatsModalOpen(false);
              subscriptionIdToCancelRef.current =
                subscriptionData?.subscriptionId;
              handleRazorpaySubscription({
                plan: chosenPlanRef.current,
                totalSeats,
                modifySeats: true,
                callback: async () => {
                  await handleCancelPlan({
                    reload: false,
                    subscriptionId: subscriptionIdToCancelRef.current,
                    message: 'Previous subscription cancelled successfully.',
                  });
                  await handleRefundForDomesticPayment();
                },
              });
            }}
          />
        </Modal>
        <Modal
          isVisible={isCurrencyModalOpen}
          hideCloseButton={geoLookupFailed}
          disableBackdropClick={geoLookupFailed}
          disableEscapeKey={geoLookupFailed}
          onClose={() => {
            setPendingCurrency(selectedCurrency);
            setIsCurrencyModalOpen(false);
          }}
        >
          <div className={styles.currencyModal}>
            <Text
              variant={textVariantsEnum.Text_lg}
              fontWeight={fontWeightEnum.Bold}
              color="gray-900"
            >
              Choose payment currency
            </Text>
            <Text
              variant={textVariantsEnum.Text_sm}
              fontWeight={fontWeightEnum.Normal}
              color="gray-500"
            >
              Choose the currency in which you want to make the payment.
            </Text>
            <div className={styles.currencyModalActions}>
              <button
                type="button"
                className={`${styles.currencyModalBtn} ${pendingCurrency === CURRENCIES.INR ? styles.currencyModalBtn_active : ''}`}
                onClick={() => handleCurrencyChoice(CURRENCIES.INR)}
              >
                INR
              </button>
              <button
                type="button"
                className={`${styles.currencyModalBtn} ${pendingCurrency === CURRENCIES.USD ? styles.currencyModalBtn_active : ''}`}
                onClick={() => handleCurrencyChoice(CURRENCIES.USD)}
              >
                USD
              </button>
            </div>
            {pendingCurrency === CURRENCIES.USD ? (
              <div className={styles.currencyModalWarning}>
                If you are making this payment from India, e-mandate in USD
                requires an internationally issued card.
              </div>
            ) : null}
            <button
              type="button"
              className={styles.currencyModalConfirm}
              onClick={handleConfirmCurrencyChoice}
              disabled={!pendingCurrency}
            >
              Confirm
            </button>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default SubscriptionPage;
